import { useEffect, useState } from 'react';
import type { OrderDetail, OrderListItem, OrderPage } from '@/api/types';
import { api } from '@/api/client';
import { num } from '@/lib/format';
import { OrderExceptionCreateDialog } from '../orders/OrderExceptionCreateDialog';
import s from './ExceptionCreateFlow.module.css';

const PAGE_SIZE = 20;

export function ExceptionCreateFlow({ initialOrderId, initialShipmentId, onClose, onSuccess }: {
  initialOrderId?: string;
  initialShipmentId?: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [draft, setDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<OrderPage>();
  const [listError, setListError] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [order, setOrder] = useState<OrderDetail>();
  const [orderError, setOrderError] = useState('');
  const [loadingOrder, setLoadingOrder] = useState(false);

  useEffect(() => {
    if (initialOrderId) return;
    const controller = new AbortController();
    setLoadingList(true);
    setListError('');
    void api.get<OrderPage>('/api/orders', { keyword: keyword || undefined, page, pageSize: PAGE_SIZE }, controller.signal)
      .then((value) => setResult(value))
      .catch((cause: Error) => { if (cause.name !== 'AbortError') setListError(cause.message); })
      .finally(() => { if (!controller.signal.aborted) setLoadingList(false); });
    return () => controller.abort();
  }, [initialOrderId, keyword, page]);

  useEffect(() => {
    if (!initialOrderId) return;
    const controller = new AbortController();
    setLoadingOrder(true);
    setOrderError('');
    void api.get<OrderDetail>(`/api/v2/orders/${encodeURIComponent(initialOrderId)}`, undefined, controller.signal)
      .then((value) => {
        if (!value.shipments?.some((batch) => batch.lines.some((line) => num(line.quantity) > 0))) {
          setOrderError('该订单没有已出库明细，不能登记异常。');
          return;
        }
        setOrder(value);
      })
      .catch((cause: Error) => { if (cause.name !== 'AbortError') setOrderError(cause.message); })
      .finally(() => { if (!controller.signal.aborted) setLoadingOrder(false); });
    return () => controller.abort();
  }, [initialOrderId]);

  const choose = async (item: OrderListItem) => {
    if (loadingOrder || !item.lines.some((line) => num(line.shippedQuantity) > 0)) return;
    setLoadingOrder(true);
    setOrderError('');
    try {
      const value = await api.get<OrderDetail>(`/api/v2/orders/${encodeURIComponent(item.id)}`);
      if (!value.shipments?.some((batch) => batch.lines.some((line) => num(line.quantity) > 0))) {
        setOrderError('该订单没有已出库明细，不能登记异常。');
        return;
      }
      setOrder(value);
    } catch (cause) {
      setOrderError(cause instanceof Error ? cause.message : '订单读取失败');
    } finally {
      setLoadingOrder(false);
    }
  };

  if (order) return <OrderExceptionCreateDialog order={order} initialShipmentId={initialShipmentId} canCreate onClose={onClose} onSuccess={onSuccess} />;

  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label="登记异常，选择订单">
      <header className={s.head}><button type="button" onClick={onClose}>取消</button><b>登记异常</b><span /></header>
      <div className={s.body}>
        <div className={s.notice}><strong>只有发过货的订单才能登记异常</strong><span>少发、破损、退货针对已出库商品；未发货订单请走订单变更或取消。</span></div>
        {!initialOrderId && <>
          <form className={s.search} onSubmit={(event) => { event.preventDefault(); setPage(1); setKeyword(draft.trim()); }}>
            <input value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="搜索订单号 / 客户 / 产品" aria-label="搜索订单" />
            <button type="submit">搜索</button>
          </form>
          <div className={s.list}>
            {loadingList && <p className={s.message}>正在读取订单…</p>}
            {listError && <p className={s.error} role="alert">{listError}</p>}
            {!loadingList && !listError && result?.items.length === 0 && <p className={s.message}>没有找到订单。</p>}
            {!loadingList && !listError && result?.items.map((item) => {
              const eligible = item.lines.some((line) => num(line.shippedQuantity) > 0);
              return <button type="button" key={item.id} disabled={!eligible || loadingOrder} onClick={() => void choose(item)}>
                <b>{item.orderNo}</b><span>{item.customer.name}</span><small>{eligible ? '已发货 · 选择' : '尚未发货，不能登记'}</small>
              </button>;
            })}
          </div>
          <footer className={s.pager}><button type="button" disabled={!result?.pagination.hasPrev || loadingList} onClick={() => setPage((value) => value - 1)}>上一页</button><span>第 {page} 页 · 共 {result?.pagination.total ?? 0} 张订单</span><button type="button" disabled={!result?.pagination.hasNext || loadingList} onClick={() => setPage((value) => value + 1)}>下一页</button></footer>
        </>}
        {loadingOrder && <p className={s.message}>正在读取已出库明细…</p>}
        {orderError && <p className={s.error} role="alert">{orderError}</p>}
      </div>
    </section>
  </div>;
}
